import {
  CellClickedEvent,
  CellContextMenuEvent,
  CellSelectionChangedEvent,
  Column,
  GridApi,
} from 'ag-grid-community';

/**
 * Excel-like row interactions on top of AG Grid cell selection: whole-row
 * selection from row header columns, and focusing a
 * newly inserted row. All helpers are generic over the row type and opt-in per grid.
 */

/**
 * True for AG Grid row-drag handle columns (colDef.rowDrag). These columns are for
 * dragging rows only: they never start a cell selection or open the row context
 * menu, but they are still part of a whole-row highlight.
 */
export function isRowDragColumn(column: Column | null | undefined): boolean {
  return Boolean(column?.getColDef().rowDrag);
}

/** Clears any cell ranges and the focused cell. */
function clearCellSelectionAndFocus<T>(api: GridApi<T>): void {
  if (api.getCellRanges()?.length) {
    api.clearCellSelection();
  }
  api.clearFocusedCell();
}

/** True when the current cell range spans every displayed column. */
function isWholeRowSelection<T>(api: GridApi<T>): boolean {
  const range = api.getCellRanges()?.[0];
  return (
    !!range && range.columns.length === api.getAllDisplayedColumns().length
  );
}

/** Replaces the current cell selection with a full-width range covering one row. */
function selectWholeRow<T>(api: GridApi<T>, rowIndex: number): void {
  api.clearCellSelection();
  api.addCellRange({
    rowStartIndex: rowIndex,
    rowEndIndex: rowIndex,
    columns: api.getAllDisplayedColumns(),
  });
}

/**
 * Excel-like row headers on top of AG Grid cell selection.
 * Returns an onCellSelectionChanged handler (cellSelection must be enabled):
 * - A drag that starts in one of `rowHeaderColIds` becomes a full-width range (entire rows).
 * - A drag that starts in a row-drag column is cleared.
 * - Any other drag is left as a normal cell range.
 * Runs only when a drag ends (started=false, finished=true); acting on mousedown
 * (started=true) would remove the range AG Grid is still extending.
 */
export function selectEntireRowsFromColumns<T>(
  rowHeaderColIds: readonly string[]
): (event: CellSelectionChangedEvent<T>) => void {
  return (event) => {
    if (event.started || !event.finished) {
      return;
    }

    const { api } = event;
    const range = api.getCellRanges()?.[0];
    const startRow = range?.startRow;
    const endRow = range?.endRow;
    if (
      !range ||
      !startRow ||
      !endRow ||
      startRow.rowPinned ||
      endRow.rowPinned
    ) {
      return;
    }

    // Must come first: whole-row ranges created by addCellRange start in the first
    // displayed column (the drag column) and must be kept. Also stops the loop.
    if (isWholeRowSelection(api)) {
      return;
    }

    if (isRowDragColumn(range.startColumn)) {
      clearCellSelectionAndFocus(api);
      return;
    }

    if (!rowHeaderColIds.includes(range.startColumn.getColId())) {
      return;
    }

    api.clearCellSelection();
    api.addCellRange({
      rowStartIndex: startRow.rowIndex,
      rowEndIndex: endRow.rowIndex,
      columns: api.getAllDisplayedColumns(),
    });
  };
}

/**
 * Excel-like row header click: a plain click on a cell in `rowHeaderColId`
 * selects the entire row; a click on a row-drag column selects nothing.
 * Use as a grid's onCellClicked handler (cellSelection must be enabled).
 * A drag that ends on another cell does not raise a click on a single
 * cell, so drag selection is unaffected.
 */
export function selectEntireRowOnClick<T>(
  rowHeaderColId: string
): (event: CellClickedEvent<T>) => void {
  return (event) => {
    const { api, column, rowIndex, node, event: domEvent } = event;

    if (isRowDragColumn(column)) {
      clearCellSelectionAndFocus(api);
      return;
    }

    const hasModifierKey =
      domEvent instanceof MouseEvent &&
      (domEvent.shiftKey || domEvent.ctrlKey || domEvent.metaKey);

    if (
      column.getColId() !== rowHeaderColId ||
      rowIndex === null ||
      node.rowPinned ||
      hasModifierKey
    ) {
      return;
    }

    selectWholeRow(api, rowIndex);
  };
}

/**
 * Excel-like row header right-click: a right-click on a cell in `rowHeaderColId`
 * selects the entire row, unless the click landed inside a selection that already
 * spans several rows or this whole row (Excel keeps those). A right-click on a
 * row-drag column opens no menu and keeps only an existing whole-row highlight.
 * Use as a grid's onCellContextMenu handler (cellSelection must be enabled).
 */
export function selectEntireRowOnContextMenu<T>(
  rowHeaderColId: string
): (event: CellContextMenuEvent<T>) => void {
  return (event) => {
    const { api, column, rowIndex, node, event: domEvent } = event;

    if (isRowDragColumn(column)) {
      // No browser menu either: the drag column has no menu at all.
      if (domEvent instanceof MouseEvent) {
        domEvent.preventDefault();
      }
      if (!isWholeRowSelection(api)) {
        clearCellSelectionAndFocus(api);
      }
      return;
    }

    if (
      column.getColId() !== rowHeaderColId ||
      rowIndex === null ||
      node.rowPinned
    ) {
      return;
    }

    const range = api.getCellRanges()?.[0];
    const startRow = range?.startRow;
    const endRow = range?.endRow;
    const spansMultipleRows =
      !!startRow && !!endRow && startRow.rowIndex !== endRow.rowIndex;
    const isThisRowFullWidth =
      isWholeRowSelection(api) &&
      startRow?.rowIndex === rowIndex &&
      endRow?.rowIndex === rowIndex;

    if (spansMultipleRows || isThisRowFullWidth) {
      return;
    }

    selectWholeRow(api, rowIndex);
  };
}

/**
 * Brings a newly inserted row into view and focuses its first editable cell.
 * - Scrolls vertically only, so the row is visible.
 * - Never scrolls horizontally: the cell is focused only if its column is already
 *   fully in view (or pinned); otherwise the grid stays where the user left it.
 * Focus only: editing is not started. Does nothing if the row is not displayed
 * (e.g. hidden by a filter).
 */
export function focusFirstEditableCell<T>(
  api: GridApi<T>,
  rowId: string
): void {
  const node = api.getRowNode(rowId);
  const rowIndex = node?.rowIndex;
  if (!node || rowIndex === null || rowIndex === undefined) {
    return;
  }

  api.ensureIndexVisible(rowIndex);

  const column = api
    .getAllDisplayedColumns()
    .find((col) => col.isCellEditable(node));
  if (!column || !isColumnFullyInView(api, column)) {
    return;
  }

  if (api.getCellRanges()?.length) {
    api.clearCellSelection();
  }
  api.setFocusedCell(rowIndex, column);
}

/** Pinned columns are always in view; scrolling columns must lie within the visible horizontal range. */
function isColumnFullyInView<T>(api: GridApi<T>, column: Column): boolean {
  if (column.isPinned()) {
    return true;
  }
  const left = column.getLeft();
  if (left === null) {
    return false;
  }
  const { left: viewLeft, right: viewRight } = api.getHorizontalPixelRange();
  return left >= viewLeft && left + column.getActualWidth() <= viewRight;
}
